import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Image,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import backIcon from '../../assets/icons/back.png';

interface TermsOfServiceScreenProps {
  navigation: NavigationProp<any>;
}

const TermsOfServiceScreen: React.FC<TermsOfServiceScreenProps> = ({ navigation }) => {
  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Terms of Service</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <Text style={styles.lastUpdated}>Last Updated: January 2, 2025</Text>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>1. Agreement to Terms</Text>
          <Text style={styles.paragraph}>
            By accessing or using the MyFinalKey mobile application and services ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
          </Text>
          <Text style={styles.paragraph}>
            These Terms apply to all users of the Service, including without limitation users who are browsers, subscribers, and contributors of content.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>2. Description of Service</Text>
          <Text style={styles.paragraph}>
            MyFinalKey provides a secure digital platform for end-of-life planning, document management, and information organization. Our Service includes:
          </Text>
          <Text style={styles.bulletPoint}>• Secure storage of personal, medical, financial, and legal information</Text>
          <Text style={styles.bulletPoint}>• Document management with encryption</Text>
          <Text style={styles.bulletPoint}>• Password and credential management</Text>
          <Text style={styles.bulletPoint}>• Contact and expense tracking</Text>
          <Text style={styles.bulletPoint}>• Successor and keyholder designation</Text>
          <Text style={styles.bulletPoint}>• Task checklists for end-of-life planning</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>3. User Accounts</Text>

          <Text style={styles.subsectionTitle}>3.1 Account Creation</Text>
          <Text style={styles.paragraph}>
            To use our Service, you must create an account. You agree to:
          </Text>
          <Text style={styles.bulletPoint}>• Provide accurate, current, and complete information</Text>
          <Text style={styles.bulletPoint}>• Maintain and promptly update your account information</Text>
          <Text style={styles.bulletPoint}>• Maintain the security of your password</Text>
          <Text style={styles.bulletPoint}>• Accept responsibility for all activities under your account</Text>
          <Text style={styles.bulletPoint}>• Notify us immediately of any unauthorized use</Text>

          <Text style={styles.subsectionTitle}>3.2 Account Eligibility</Text>
          <Text style={styles.paragraph}>
            You must be at least 18 years old to use this Service. By agreeing to these Terms, you represent and warrant that you are at least 18 years of age.
          </Text>

          <Text style={styles.subsectionTitle}>3.3 Account Termination</Text>
          <Text style={styles.paragraph}>
            We reserve the right to refuse service, terminate accounts, or remove or edit content at our sole discretion, with or without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>4. Subscription Plans and Billing</Text>

          <Text style={styles.subsectionTitle}>4.1 Subscription Tiers</Text>
          <Text style={styles.paragraph}>
            We offer multiple subscription tiers:
          </Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Free:</Text> Checklist functionality only</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Local Storage ($10/year):</Text> Local data storage on device</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Cloud Backup ($99/year):</Text> Local and cloud-encrypted storage</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.bold}>Hard Copy ($129/year):</Text> All features plus physical backup</Text>

          <Text style={styles.subsectionTitle}>4.2 Payment Terms</Text>
          <Text style={styles.paragraph}>
            Subscription fees are charged annually in advance. All fees are non-refundable unless otherwise stated. We reserve the right to change our subscription fees at any time with 30 days' notice to existing subscribers.
          </Text>

          <Text style={styles.subsectionTitle}>4.3 Automatic Renewal</Text>
          <Text style={styles.paragraph}>
            Your subscription will automatically renew at the end of each billing period unless you cancel before the renewal date. You may cancel your subscription at any time through the app settings.
          </Text>

          <Text style={styles.subsectionTitle}>4.4 Free Trial</Text>
          <Text style={styles.paragraph}>
            We may offer free trials for paid subscriptions. At the end of the trial period, your subscription will automatically convert to a paid subscription unless you cancel before the trial ends.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>5. User Content and Data</Text>

          <Text style={styles.subsectionTitle}>5.1 Your Content</Text>
          <Text style={styles.paragraph}>
            You retain all ownership rights to the content you upload, create, or store in the Service ("Your Content"). By using the Service, you grant us a limited license to store, backup, and display Your Content solely for the purpose of providing the Service.
          </Text>

          <Text style={styles.subsectionTitle}>5.2 Content Responsibility</Text>
          <Text style={styles.paragraph}>
            You are solely responsible for Your Content and the consequences of posting or publishing it. We do not endorse any Content or any opinion, recommendation, or advice expressed therein.
          </Text>

          <Text style={styles.subsectionTitle}>5.3 Prohibited Content</Text>
          <Text style={styles.paragraph}>
            You agree not to upload, post, or transmit any content that:
          </Text>
          <Text style={styles.bulletPoint}>• Is illegal, fraudulent, or violates any laws</Text>
          <Text style={styles.bulletPoint}>• Infringes on intellectual property rights</Text>
          <Text style={styles.bulletPoint}>• Contains malware or harmful code</Text>
          <Text style={styles.bulletPoint}>• Is defamatory, obscene, or offensive</Text>
          <Text style={styles.bulletPoint}>• Violates the privacy of others</Text>

          <Text style={styles.subsectionTitle}>5.4 Data Backup</Text>
          <Text style={styles.paragraph}>
            While we implement robust backup systems for cloud-stored data, we recommend maintaining your own backups of critical information. We are not responsible for any loss of data.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>6. Intellectual Property Rights</Text>
          <Text style={styles.paragraph}>
            The Service and its original content (excluding Your Content), features, and functionality are owned by MyFinalKey and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
          </Text>
          <Text style={styles.paragraph}>
            Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>7. Prohibited Uses</Text>
          <Text style={styles.paragraph}>
            You agree not to use the Service:
          </Text>
          <Text style={styles.bulletPoint}>• For any unlawful purpose or in violation of these Terms</Text>
          <Text style={styles.bulletPoint}>• To impersonate or attempt to impersonate another person</Text>
          <Text style={styles.bulletPoint}>• To engage in any activity that interferes with the Service</Text>
          <Text style={styles.bulletPoint}>• To attempt to gain unauthorized access to the Service</Text>
          <Text style={styles.bulletPoint}>• To use any automated system to access the Service</Text>
          <Text style={styles.bulletPoint}>• To introduce viruses, malware, or harmful code</Text>
          <Text style={styles.bulletPoint}>• To collect information about other users</Text>
          <Text style={styles.bulletPoint}>• To reverse engineer or decompile the Service</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>8. Successor and Keyholder Access</Text>

          <Text style={styles.subsectionTitle}>8.1 Designation Rights</Text>
          <Text style={styles.paragraph}>
            You may designate successors and keyholders to access your information according to your specified conditions. You are responsible for:
          </Text>
          <Text style={styles.bulletPoint}>• Accurately identifying your successors and keyholders</Text>
          <Text style={styles.bulletPoint}>• Defining appropriate access levels and permissions</Text>
          <Text style={styles.bulletPoint}>• Updating designations as circumstances change</Text>
          <Text style={styles.bulletPoint}>• Communicating your wishes to designated individuals</Text>

          <Text style={styles.subsectionTitle}>8.2 Access Conditions</Text>
          <Text style={styles.paragraph}>
            Access by successors and keyholders will be granted according to the conditions you establish, which may include time-based triggers, verification requirements, or other specified events.
          </Text>

          <Text style={styles.subsectionTitle}>8.3 Verification Process</Text>
          <Text style={styles.paragraph}>
            We reserve the right to require verification of identity and authorization before granting successor or keyholder access to your account and information.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>9. Privacy and Data Protection</Text>
          <Text style={styles.paragraph}>
            Your privacy is important to us. Our use of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your information.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>10. Security</Text>
          <Text style={styles.paragraph}>
            We implement industry-standard security measures to protect your information. However, no method of transmission or storage is 100% secure. We cannot guarantee absolute security of your data.
          </Text>
          <Text style={styles.paragraph}>
            You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>11. Disclaimer of Warranties</Text>
          <Text style={styles.paragraph}>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
          </Text>
          <Text style={styles.bulletPoint}>• Warranties of merchantability, fitness for a particular purpose</Text>
          <Text style={styles.bulletPoint}>• Non-infringement or course of performance</Text>
          <Text style={styles.bulletPoint}>• Uninterrupted or error-free operation</Text>
          <Text style={styles.bulletPoint}>• Accuracy, reliability, or completeness of content</Text>
          <Text style={styles.bulletPoint}>• Security of data transmission or storage</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>12. Limitation of Liability</Text>
          <Text style={styles.paragraph}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, MYFINALKEY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
          </Text>
          <Text style={styles.paragraph}>
            OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY, OR $100, WHICHEVER IS GREATER.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>13. Indemnification</Text>
          <Text style={styles.paragraph}>
            You agree to indemnify and hold harmless MyFinalKey and its officers, directors, employees, and agents from any claims, losses, damages, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from:
          </Text>
          <Text style={styles.bulletPoint}>• Your use of the Service</Text>
          <Text style={styles.bulletPoint}>• Your violation of these Terms</Text>
          <Text style={styles.bulletPoint}>• Your Content</Text>
          <Text style={styles.bulletPoint}>• Your violation of any rights of another</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>14. Dispute Resolution</Text>

          <Text style={styles.subsectionTitle}>14.1 Informal Resolution</Text>
          <Text style={styles.paragraph}>
            Before filing a claim, you agree to contact us and attempt to resolve the dispute informally by sending a written notice describing the facts and basis of the claim.
          </Text>

          <Text style={styles.subsectionTitle}>14.2 Binding Arbitration</Text>
          <Text style={styles.paragraph}>
            Any dispute arising from these Terms or the Service will be resolved through binding arbitration, rather than in court, except that you may assert claims in small claims court if your claims qualify.
          </Text>

          <Text style={styles.subsectionTitle}>14.3 Class Action Waiver</Text>
          <Text style={styles.paragraph}>
            You agree that disputes will be resolved on an individual basis only, and not as a class action, class arbitration, or other representative action.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>15. Governing Law</Text>
          <Text style={styles.paragraph}>
            These Terms shall be governed by and construed in accordance with the laws of Texas, USA, without regard to its conflict of law provisions.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>16. Changes to Terms</Text>
          <Text style={styles.paragraph}>
            We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the new Terms on this page and updating the "Last Updated" date.
          </Text>
          <Text style={styles.paragraph}>
            Your continued use of the Service after changes become effective constitutes your acceptance of the revised Terms.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>17. Severability</Text>
          <Text style={styles.paragraph}>
            If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining provisions will continue in full force and effect.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>18. Entire Agreement</Text>
          <Text style={styles.paragraph}>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and MyFinalKey regarding the Service and supersede all prior agreements and understandings.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>19. Contact Information</Text>
          <Text style={styles.paragraph}>
            If you have any questions about these Terms, please contact us at:
          </Text>
          <Text style={styles.contactInfo}>Email: legal@myfinalkey.com</Text>
          <Text style={styles.contactInfo}>Address: Southlake, TX 76092</Text>
          <Text style={styles.contactInfo}>Phone: 469-734-2767</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.acknowledgment}>
            By using MyFinalKey, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </Text>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(15),
    paddingVertical: ms(15),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: ms(1),
    borderBottomColor: '#E5E7EB',
  },
  backButton: {
    padding: ms(5),
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#1C2C4F',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  placeholder: {
    width: s(34),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  lastUpdated: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: ms(15),
    marginBottom: ms(10),
  },
  section: {
    marginBottom: ms(25),
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(12),
  },
  subsectionTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginTop: ms(10),
    marginBottom: ms(8),
  },
  paragraph: {
    fontSize: ms(14, 0.3),
    color: '#4B5563',
    lineHeight: ms(22, 0.3),
    marginBottom: ms(10),
  },
  bulletPoint: {
    fontSize: ms(14, 0.3),
    color: '#4B5563',
    lineHeight: ms(22, 0.3),
    marginBottom: ms(6),
    marginLeft: ms(10),
  },
  bold: {
    fontWeight: '600',
    color: '#1C2C4F',
  },
  contactInfo: {
    fontSize: ms(14, 0.3),
    color: '#29BFC0',
    lineHeight: ms(22, 0.3),
    marginBottom: ms(4),
  },
  acknowledgment: {
    fontSize: ms(14, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: ms(10),
  },
  bottomPadding: {
    height: s(40),
  },
});

export default TermsOfServiceScreen;
